import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import { normalizeBackendJobStatus, toBackendJobStatus, type BackendJobStatus } from '@/lib/job-status'
import { getMapSearchCity } from '@/lib/map-location'
import { Job, JobAcceptanceMode, JobCreatePayload, JobPayrollSummary, PaginatedResponse, ApiResponse, JobLocation, JobMediaItem, JobManagementConfiguration, JobManagementConfigurationUpdatePayload, JobAuditEntry, JobAuditActionOption, type EventTag } from '@/types'

const JOB_PRIORITIES: Job['priority'][] = ['low', 'medium', 'high', 'urgent', 'critical']
const DEFAULT_JOB_PRIORITY: Job['priority'] = 'medium'
const JOB_ACCEPTANCE_MODES: JobAcceptanceMode[] = ['manual', 'auto']
const DEFAULT_JOB_ACCEPTANCE_MODE: JobAcceptanceMode = 'manual'

const normalizeJobAcceptanceMode = (value?: string | null): JobAcceptanceMode =>
  JOB_ACCEPTANCE_MODES.includes(value as JobAcceptanceMode)
    ? (value as JobAcceptanceMode)
    : DEFAULT_JOB_ACCEPTANCE_MODE

type BackendJob = Omit<Job, 'status' | 'priority'> & {
  client?: string
  client_user?: number | string | null
  client_user_info?: {
    id?: number | string
    name?: string
    email?: string | null
    phone?: string | null
  } | null
  job_ref?: string | null
  job_code?: string | null
  job_landmark?: string | null
  parent_job?: number | string | null
  parent_event_info?: Job['parent_event_info']
  project_info?: Job['project_info']
  sub_events_count?: number | string | null
  kind_code?: string | null
  tags?: EventTag[] | null
  category_name?: string | null
  category?: number | string | null
  order?: number | string | null
  order_reference_id?: string | null
  order_info?: Job['order_info'] | null
  primary_worker?: number | string | null
  team_template?: number | string | null
  team_template_info?: Job['team_template_info']
  staffed_team?: number | string | null
  staffed_team_info?: Job['staffed_team_info']
  category_info?: {
    id?: number | string
    name?: string
  } | null
  primary_worker_info?: {
    id?: number | string
    name?: string
    mobile?: string
  } | null
  scheduled_start?: string | null
  scheduled_end?: string | null
  scheduled_hours?: number | string | null
  recurrence_rule?: Job['recurrence_rule']
  recurrence_interval_days?: number | string | null
  recurrence_weekdays?: Array<number | string> | null
  recurrence_month_day?: number | string | null
  recurrence_count?: number | string | null
  recurrence_end?: string | null
  recurrence_parent_info?: Job['recurrence_parent_info']
  next_occurrence_info?: Job['next_occurrence_info']
  job_pay?: number | string | null
  advance_payment?: number | string | null
  total_advance_payment?: number | string | null
  invoice_amount_due?: number | string | null
  job_expenses?: number | string | null
  first_line?: string
  second_line?: string
  door?: string
  city?: string
  country?: string
  postcode?: string
  customer_name?: string | null
  job_location?: number | string | null
  location_info?: JobLocation | null
  email?: string
  phone?: string
  status: BackendJobStatus
  priority?: string
}

type BackendJobMediaItem = JobMediaItem & {
  file?: string | null
  file_url?: string | null
}

type BackendJobManagementConfiguration = Partial<JobManagementConfiguration>

const toNumber = (value: number | string | null | undefined): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const trimText = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const combineDateAndTime = (date?: string, time?: string, endOfDay = false): string | undefined => {
  const normalizedDate = trimText(date)
  if (!normalizedDate) {
    return undefined
  }

  const normalizedTime = trimText(time) || (endOfDay ? '23:59:59' : '00:00:00')
  const withSeconds = normalizedTime.length === 5 ? `${normalizedTime}:00` : normalizedTime
  return `${normalizedDate}T${withSeconds}`
}

const toDateTime = (value?: string, endOfDay = false): string | undefined => {
  const date = trimText(value)
  if (!date) {
    return undefined
  }

  if (date.includes('T')) {
    return date
  }

  return `${date}T${endOfDay ? '23:59:59' : '00:00:00'}`
}

const normalizeJobSummary = (summary?: Job['job_summary'] | null): Job['job_summary'] | undefined => {
  if (!summary) {
    return undefined
  }

  return {
    total_expenses: toNumber(summary.total_expenses) ?? 0,
    work_days_count: toNumber(summary.work_days_count) ?? 0,
    total_hours: toNumber(summary.total_hours) ?? 0,
    total_workers: toNumber(summary.total_workers) ?? 0,
    internal_workers: toNumber(summary.internal_workers) ?? 0,
    contract_workers: toNumber(summary.contract_workers) ?? 0,
  }
}

const resolveJobDisplayTitle = (job: BackendJob): string => {
  const variantName = trimText(job.order_item_info?.product_variant_name)
  if (variantName) {
    return variantName
  }

  if (job.order_item_info) {
    const productName = trimText(job.order_item_info.product_name)
    if (productName) {
      return productName
    }
  }

  const orderReference = trimText(job.order_reference_id) ?? trimText(job.order_info?.reference_id)
  const orderTypeLabel = trimText(job.order_info?.type_label)
  if (orderReference && orderTypeLabel) {
    return `${orderReference} - ${orderTypeLabel}`
  }

  return trimText(job.title) ?? 'Untitled job'
}

const buildJobAddress = (job: BackendJob) => {
  const addressParts = [
    trimText(job.door),
    trimText(job.first_line),
    trimText(job.second_line),
    trimText(job.city),
    trimText(job.country),
    trimText(job.postcode),
  ].filter(Boolean)

  return trimText(job.address)
    ?? (addressParts.length > 0 ? addressParts.join(', ') : undefined)
    ?? trimText(job.location_info?.place)
    ?? trimText(job.order_info?.address)
}

const resolveJobCustomerName = (job: BackendJob) =>
  trimText(job.client_name)
  ?? trimText(job.client_user_info?.name)
  ?? trimText(job.customer_name)
  ?? trimText(job.order_info?.customer_name)
  ?? trimText(job.client)

const normalizeJob = (job: BackendJob): Job => {
  const location = trimText(job.location_info?.place) ?? trimText(job.city) ?? trimText(job.location)
  const address = buildJobAddress(job)
  const clientName = resolveJobCustomerName(job)
  const normalizedJobSummary = normalizeJobSummary(job.job_summary)
  const workerPayTotal = toNumber(job.worker_pay_total) ?? 0
  const recordedExpenses = Math.max(
    toNumber(job.job_expenses) ?? 0,
    normalizedJobSummary?.total_expenses ?? 0,
  )
  const expenseCandidates = [
    toNumber(job.actual_cost),
    toNumber(job.job_expenses),
    normalizedJobSummary?.total_expenses,
  ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  const actualSpentCandidates = [
    toNumber(job.total_actual_spent),
    workerPayTotal + recordedExpenses,
  ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value))

  return {
    ...job,
    title: resolveJobDisplayTitle(job),
    order: job.order ?? null,
    order_reference_id: trimText(job.order_reference_id) ?? null,
    order_info: job.order_info
      ? {
          ...job.order_info,
          reference_id: trimText(job.order_info.reference_id) ?? '',
          customer_name: trimText(job.order_info.customer_name) ?? null,
          email: trimText(job.order_info.email) ?? null,
          phone: trimText(job.order_info.phone) ?? null,
          address: trimText(job.order_info.address) ?? null,
        }
      : null,
    job_ref: trimText(job.job_ref),
    job_code: trimText(job.job_code) ?? null,
    job_landmark: trimText(job.job_landmark) ?? null,
    parent_job: job.parent_job ?? null,
    parent_event_info: job.parent_event_info ?? null,
    project_info: job.project_info ?? null,
    sub_events_count: toNumber(job.sub_events_count) ?? 0,
    kind_code: trimText(job.kind_code) ?? null,
    tags: Array.isArray(job.tags) ? job.tags : [],
    category: job.category ?? undefined,
    category_name: trimText(job.category_name) ?? trimText(job.category_info?.name),
    primary_worker: job.primary_worker ?? undefined,
    team_template: job.team_template ?? null,
    team_template_info: job.team_template_info ?? null,
    staffed_team: job.staffed_team ?? null,
    staffed_team_info: job.staffed_team_info ?? null,
    primary_worker_info: job.primary_worker_info ?? null,
    client_user: job.client_user ?? null,
    client_user_info: job.client_user_info ?? null,
    status: normalizeBackendJobStatus(job.status),
    priority: ['low', 'medium', 'high', 'urgent'].includes(job.priority ?? '')
      ? (job.priority as Job['priority'])
      : DEFAULT_JOB_PRIORITY,
    client_name: clientName,
    client_contact: trimText(job.client_contact) ?? trimText(job.email) ?? trimText(job.phone) ?? trimText(job.client_user_info?.email) ?? trimText(job.client_user_info?.phone) ?? trimText(job.order_info?.email) ?? trimText(job.order_info?.phone),
    email: trimText(job.email) ?? trimText(job.client_user_info?.email) ?? trimText(job.order_info?.email),
    phone: trimText(job.phone) ?? trimText(job.client_user_info?.phone) ?? trimText(job.order_info?.phone),
    location,
    location_id: job.job_location ?? null,
    location_info: job.location_info ?? null,
    latitude: toNumber(job.latitude) ?? toNumber(job.location_info?.latitude) ?? null,
    longitude: toNumber(job.longitude) ?? toNumber(job.location_info?.longitude) ?? null,
    address,
    scheduled_start: trimText(job.scheduled_start) ?? trimText(job.start_date),
    scheduled_end: trimText(job.scheduled_end) ?? trimText(job.end_date),
    start_date: trimText(job.start_date) ?? trimText(job.scheduled_start),
    end_date: trimText(job.end_date) ?? trimText(job.scheduled_end),
    recurrence_rule: job.recurrence_rule ?? 'none',
    recurrence_interval_days: toNumber(job.recurrence_interval_days) ?? null,
    recurrence_weekdays: Array.isArray(job.recurrence_weekdays)
      ? job.recurrence_weekdays.map((day) => Number(day)).filter((day) => Number.isInteger(day))
      : [],
    recurrence_month_day: toNumber(job.recurrence_month_day) ?? null,
    recurrence_count: toNumber(job.recurrence_count) ?? null,
    recurrence_end: trimText(job.recurrence_end) ?? null,
    recurrence_parent_info: job.recurrence_parent_info ?? null,
    next_occurrence_info: job.next_occurrence_info ?? null,
    estimated_hours: job.estimated_hours ?? toNumber(job.scheduled_hours),
    budget: job.budget ?? toNumber(job.job_pay),
    advance_payment: toNumber(job.advance_payment),
    total_advance_payment: toNumber(job.total_advance_payment) ?? toNumber(job.advance_payment),
    invoice_amount_due: toNumber(job.invoice_amount_due),
    payment_due: toNumber(job.payment_due),
    actual_cost: expenseCandidates.length > 0 ? Math.max(...expenseCandidates) : undefined,
    worker_pay_total: workerPayTotal,
    total_estimated_spent: toNumber(job.total_estimated_spent),
    total_actual_spent: actualSpentCandidates.length > 0 ? Math.max(...actualSpentCandidates) : undefined,
    job_summary: normalizedJobSummary,
    assignment_acceptance_mode: normalizeJobAcceptanceMode(job.assignment_acceptance_mode),
    workday_entry_acceptance_mode: normalizeJobAcceptanceMode(job.workday_entry_acceptance_mode),
    workday_entry_approval_mode: normalizeJobAcceptanceMode(job.workday_entry_approval_mode),
  }
}

const DEFAULT_JOB_MANAGEMENT_CONFIGURATION: JobManagementConfiguration = {
  track_worker: true,
  push_notification: true,
  sms_notification: false,
  email_notification: true,
  number_gallery_files: 10,
  maximum_file_size: 10 * 1024 * 1024,
  work_hours_per_day: 8.5,
  allow_worker_comments: true,
  require_otp_for_completion: true,
  auto_assign_primary_worker: true,
  allow_expense_uploads: true,
  allow_multiple_assignments: true,
  enable_job_audit: false,
  media_base_url: null,
  gallery_storage_backend: 'local',
}

const resolveMediaUrl = (fileUrl?: string | null, filePath?: string | null, mediaBaseUrl?: string | null) => {
  const backendUrl = String(fileUrl ?? '').trim()
  if (backendUrl.startsWith('http://') || backendUrl.startsWith('https://')) {
    return backendUrl
  }

  const storedPath = String(filePath ?? fileUrl ?? '').trim()
  if (!storedPath) {
    return undefined
  }

  if (storedPath.startsWith('http://') || storedPath.startsWith('https://')) {
    return storedPath
  }

  if (mediaBaseUrl) {
    return `${mediaBaseUrl.replace(/\/$/, '')}/${storedPath.replace(/^\//, '')}`
  }

  const apiBase = typeof api.defaults.baseURL === 'string' && api.defaults.baseURL.length > 0
    ? api.defaults.baseURL.replace(/\/$/, '')
    : window.location.origin

  const normalizedPath = storedPath.startsWith('/uploads/')
    ? storedPath
    : storedPath.startsWith('/')
      ? storedPath
      : `/uploads/${storedPath.replace(/^\//, '')}`
  return `${apiBase}${normalizedPath}`
}

const normalizeJobMediaItem = (
  item: BackendJobMediaItem,
  mediaBaseUrl?: string | null,
): JobMediaItem => {
  const resolvedFileUrl = resolveMediaUrl(item.file_url, item.file, mediaBaseUrl) ?? null

  return {
    ...item,
    file: resolvedFileUrl,
    file_name: item.file_name ?? (item.file ? item.file.split('/').at(-1) ?? null : null),
    file_type: item.file_type ?? 'other',
    file_url: resolvedFileUrl,
    is_before: item.is_before === true || String(item.is_before).toLowerCase() === 'true' || String(item.is_before) === '1',
    is_after: item.is_after === true || String(item.is_after).toLowerCase() === 'true' || String(item.is_after) === '1',
  }
}

const normalizeJobManagementConfiguration = (
  config?: BackendJobManagementConfiguration | null,
): JobManagementConfiguration => ({
  ...DEFAULT_JOB_MANAGEMENT_CONFIGURATION,
  ...config,
  number_gallery_files: Number(config?.number_gallery_files ?? DEFAULT_JOB_MANAGEMENT_CONFIGURATION.number_gallery_files),
  maximum_file_size: Number(config?.maximum_file_size ?? DEFAULT_JOB_MANAGEMENT_CONFIGURATION.maximum_file_size),
  work_hours_per_day: Number(config?.work_hours_per_day ?? DEFAULT_JOB_MANAGEMENT_CONFIGURATION.work_hours_per_day),
  gallery_storage_backend: config?.gallery_storage_backend === 's3' ? 's3' : 'local',
})

const normalizeJobPayload = (payload: Partial<JobCreatePayload>) => {
  const clientContact = trimText(payload.client_contact)
  const clientName = trimText(payload.client) ?? trimText(payload.client_name)
  const normalized: Record<string, unknown> = {
    title: trimText(payload.title) ?? '',
    description: trimText(payload.description) ?? '',
    category: payload.category,
    client: clientName ?? '',
    status: toBackendJobStatus(payload.status) ?? 'draft',
  }

  if (clientName) {
    normalized.customer_name = clientName
  }

  if (payload.priority !== undefined) {
    normalized.priority = JOB_PRIORITIES.includes(payload.priority as Job['priority'])
      ? payload.priority
      : DEFAULT_JOB_PRIORITY
  }

  if (payload.order_reference_id === null || payload.order_reference_id === '') {
    normalized.order_reference_id = null
  } else {
    const orderReferenceId = trimText(payload.order_reference_id)
    if (orderReferenceId) {
      normalized.order_reference_id = orderReferenceId
    }
  }

  if (payload.client_user === null) {
    normalized.client_user = null
  } else if (payload.client_user !== undefined && payload.client_user !== '') {
    normalized.client_user = payload.client_user
  }

  if (payload.primary_worker === null) {
    normalized.primary_worker = null
  } else if (payload.primary_worker !== undefined && payload.primary_worker !== '') {
    normalized.primary_worker = payload.primary_worker
  }

  if (payload.team_template === null) {
    normalized.team_template = null
  } else if (payload.team_template !== undefined && payload.team_template !== '') {
    normalized.team_template = payload.team_template
  }

  if (payload.staffed_team === null) {
    normalized.staffed_team = null
  } else if (payload.staffed_team !== undefined && payload.staffed_team !== '') {
    normalized.staffed_team = payload.staffed_team
  }

  if (payload.location_id === null) {
    normalized.job_location = null
  } else if (payload.location_id !== undefined && payload.location_id !== '') {
    normalized.job_location = payload.location_id
  }

  const scheduledStart = trimText(payload.scheduled_start)
    ?? combineDateAndTime(payload.start_date, payload.start_time)
    ?? toDateTime(payload.start_date)
  const scheduledEnd = trimText(payload.scheduled_end)
    ?? combineDateAndTime(payload.end_date, payload.end_time)
    ?? toDateTime(payload.end_date, true)
  const scheduledHours = payload.scheduled_hours ?? payload.estimated_hours
  const budget = payload.job_pay ?? payload.budget
  const advancePayment = payload.advance_payment
  const paymentDue = payload.payment_due
  const recurrenceRule = payload.recurrence_rule ?? 'none'
  const address = trimText(payload.address)
  const location = trimText(payload.location)

  if (scheduledStart) {
    normalized.scheduled_start = scheduledStart
  }

  if (scheduledEnd) {
    normalized.scheduled_end = scheduledEnd
  }

  let resolvedScheduledHours = scheduledHours
  if (resolvedScheduledHours === undefined && scheduledStart && scheduledEnd) {
    const startMs = new Date(scheduledStart).getTime()
    const endMs = new Date(scheduledEnd).getTime()
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      resolvedScheduledHours = Math.round(((endMs - startMs) / 3_600_000) * 100) / 100
    }
  }

  if (resolvedScheduledHours !== undefined) {
    normalized.scheduled_hours = resolvedScheduledHours
  }

  if (budget !== undefined) {
    normalized.job_pay = budget
  }

  if (advancePayment !== undefined) {
    normalized.advance_payment = advancePayment
  }

  if (paymentDue !== undefined) {
    normalized.payment_due = paymentDue
  }

  normalized.recurrence_rule = recurrenceRule

  if (recurrenceRule === 'every_n_days') {
    normalized.recurrence_interval_days = payload.recurrence_interval_days ?? null
  } else {
    normalized.recurrence_interval_days = null
  }

  if (recurrenceRule === 'weekly_days') {
    normalized.recurrence_weekdays = payload.recurrence_weekdays ?? []
  } else {
    normalized.recurrence_weekdays = []
  }

  if (recurrenceRule === 'monthly_day') {
    normalized.recurrence_month_day = payload.recurrence_month_day ?? null
  } else {
    normalized.recurrence_month_day = null
  }

  if (recurrenceRule !== 'none' && payload.recurrence_count) {
    normalized.recurrence_count = payload.recurrence_count
  } else {
    normalized.recurrence_count = null
  }

  if (payload.recurrence_end) {
    normalized.recurrence_end = payload.recurrence_end
  } else {
    normalized.recurrence_end = null
  }

  if (address) {
    normalized.first_line = address
  }

  const city = trimText(payload.city) ?? getMapSearchCity({ label: location })
  if (city) {
    normalized.city = city
  }

  if (payload.latitude === null || payload.latitude === '') {
    normalized.latitude = null
  } else {
    const latitude = toNumber(payload.latitude)
    if (latitude !== undefined) {
      normalized.latitude = latitude
    }
  }

  if (payload.longitude === null || payload.longitude === '') {
    normalized.longitude = null
  } else {
    const longitude = toNumber(payload.longitude)
    if (longitude !== undefined) {
      normalized.longitude = longitude
    }
  }

  if (clientContact) {
    if (clientContact.includes('@')) {
      normalized.email = clientContact
    } else {
      normalized.phone = clientContact
    }
  } else {
    const email = trimText(payload.email)
    const phone = trimText(payload.phone)

    if (email) {
      normalized.email = email
    }

    if (phone) {
      normalized.phone = phone
    }
  }

  if (payload.assignment_acceptance_mode !== undefined) {
    normalized.assignment_acceptance_mode = normalizeJobAcceptanceMode(payload.assignment_acceptance_mode)
  }

  if (payload.workday_entry_acceptance_mode !== undefined) {
    normalized.workday_entry_acceptance_mode = normalizeJobAcceptanceMode(payload.workday_entry_acceptance_mode)
  }

  if (payload.workday_entry_approval_mode !== undefined) {
    normalized.workday_entry_approval_mode = normalizeJobAcceptanceMode(payload.workday_entry_approval_mode)
  }

  const kind = trimText((payload as { kind?: string | null }).kind)
  if (kind) {
    normalized.kind = kind
  }

  const jobCode = trimText(payload.job_code)
  if (payload.job_code !== undefined) {
    normalized.job_code = jobCode ?? ''
  }

  const jobLandmark = trimText(payload.job_landmark)
  if (payload.job_landmark !== undefined) {
    normalized.job_landmark = jobLandmark ?? ''
  }

  if (payload.project_id !== undefined) {
    normalized.project_id = payload.project_id
  }

  if (payload.parent_event_id !== undefined) {
    normalized.parent_event_id = payload.parent_event_id
  }

  if (payload.tag_ids !== undefined) {
    normalized.tag_ids = payload.tag_ids
      .map((tagId) => Number(tagId))
      .filter((tagId) => Number.isFinite(tagId) && tagId > 0)
  }

  return normalized
}

export interface JobsQueryParams {
  service_instance_id?: string | number
  page?: number
  page_size?: number
  search?: string
  order_id?: string
  status?: string
  priority?: string
  recurrence_state?: 'recurring' | 'single' | 'generated' | 'source'
  category?: string | number
  schedule_state?: 'upcoming' | 'overdue' | 'in_progress'
  ordering?: string
  client_user?: string | number
  job_location_id?: string | number
  worker?: string | number
  primary?: string | number
  job_scope?: 'open' | 'completed'
  project_id?: string | number
  is_project?: boolean
  kind?: string
  top_level?: boolean
  date_filter?: 'today' | 'yesterday' | 'this_month' | 'last_month' | 'custom'
  start_date?: string
  end_date?: string
}

export interface JobAuditQueryParams {
  job?: string | number
  assignment?: string | number
  action?: string
  work_day_entry?: string | number
  page?: number
  page_size?: number
}

export const jobsApi = {
  // List all jobs with pagination and filters
  getJobs: async (params?: JobsQueryParams): Promise<PaginatedResponse<Job>> => {
    const normalizedParams = params
      ? {
          ...params,
          status: params.status === 'open' ? 'open' : toBackendJobStatus(params.status),
        }
      : undefined

    const response = await api.get<ApiResponse<PaginatedResponse<BackendJob>> | PaginatedResponse<BackendJob>>(API_PATHS.jobs.list, { params: normalizedParams })
    const payload = 'data' in response.data ? response.data.data : response.data

    return {
      ...payload,
      results: payload.results.map(normalizeJob),
    }
  },

  getProjects: async (params?: JobsQueryParams): Promise<PaginatedResponse<Job>> => {
    const normalizedParams = {
      ...(params ?? {}),
      is_project: true,
      top_level: params?.top_level ?? true,
      status: params?.status === 'open' ? 'open' : toBackendJobStatus(params?.status),
    }

    const response = await api.get<ApiResponse<PaginatedResponse<BackendJob>> | PaginatedResponse<BackendJob>>(
      API_PATHS.jobs.projectsList,
      { params: normalizedParams },
    )
    const payload = 'data' in response.data ? response.data.data : response.data

    return {
      ...payload,
      results: payload.results.map(normalizeJob),
    }
  },

  getEvents: async (params?: JobsQueryParams): Promise<PaginatedResponse<Job>> => {
    const normalizedParams = {
      ...(params ?? {}),
      kind: 'event',
      status: params?.status === 'open' ? 'open' : toBackendJobStatus(params?.status),
    }

    const response = await api.get<ApiResponse<PaginatedResponse<BackendJob>> | PaginatedResponse<BackendJob>>(
      API_PATHS.jobs.list,
      { params: normalizedParams },
    )
    const payload = 'data' in response.data ? response.data.data : response.data

    return {
      ...payload,
      results: payload.results.map(normalizeJob),
    }
  },

  getSubEvents: async (
    parentEventId: string,
    params?: Omit<JobsQueryParams, 'project_id' | 'kind'>,
  ): Promise<PaginatedResponse<Job>> => {
    return jobsApi.getEvents({
      ...(params ?? {}),
      project_id: parentEventId,
      top_level: false,
    })
  },

  // Get a single job by ID
  getJob: async (id: string, options?: { service_instance_id?: string | number }): Promise<Job> => {
    const response = await api.get<ApiResponse<BackendJob>>(API_PATHS.jobs.detail(id), {
      params: options?.service_instance_id != null
        ? { service_instance_id: options.service_instance_id }
        : undefined,
    })
    return normalizeJob(response.data.data)
  },

  getProject: async (id: string, options?: { service_instance_id?: string | number }): Promise<Job> => {
    const response = await api.get<ApiResponse<BackendJob>>(API_PATHS.jobs.projectDetail(id), {
      params: options?.service_instance_id != null
        ? { service_instance_id: options.service_instance_id }
        : undefined,
    })
    return normalizeJob(response.data.data)
  },

  getProjectWorkOrders: async (
    projectId: string,
    params?: Omit<JobsQueryParams, 'project_id' | 'is_project'>,
  ): Promise<PaginatedResponse<Job>> => {
    const normalizedParams = {
      ...(params ?? {}),
      is_project: false,
      top_level: params?.top_level ?? false,
      status: params?.status === 'open' ? 'open' : toBackendJobStatus(params?.status),
    }

    const response = await api.get<ApiResponse<PaginatedResponse<BackendJob>> | PaginatedResponse<BackendJob>>(
      API_PATHS.jobs.projectWorkOrders(projectId),
      { params: normalizedParams },
    )
    const payload = 'data' in response.data ? response.data.data : response.data

    return {
      ...payload,
      results: payload.results.map(normalizeJob),
    }
  },

  // Create a new job
  create: async (payload: JobCreatePayload): Promise<Job> => {
    const response = await api.post<ApiResponse<BackendJob>>(API_PATHS.jobs.create, normalizeJobPayload(payload))
    return normalizeJob(response.data.data)
  },

  createProject: async (payload: JobCreatePayload): Promise<Job> => {
    const response = await api.post<ApiResponse<BackendJob>>(
      API_PATHS.jobs.createProject,
      normalizeJobPayload({ ...payload, is_project: true }),
    )
    return normalizeJob(response.data.data)
  },

  createEvent: async (payload: JobCreatePayload): Promise<Job> => {
    const response = await api.post<ApiResponse<BackendJob>>(
      API_PATHS.jobs.create,
      normalizeJobPayload({ ...payload, kind: 'event' }),
    )
    return normalizeJob(response.data.data)
  },

  // Update an existing job
  update: async (id: string, payload: Partial<JobCreatePayload>): Promise<Job> => {
    const response = await api.post<ApiResponse<BackendJob>>(API_PATHS.jobs.update, { id, ...normalizeJobPayload(payload) })
    return normalizeJob(response.data.data)
  },

  updateProject: async (id: string, payload: Partial<JobCreatePayload>): Promise<Job> => {
    const response = await api.post<ApiResponse<BackendJob>>(
      API_PATHS.jobs.updateProject,
      { id, ...normalizeJobPayload(payload), is_project: true },
    )
    return normalizeJob(response.data.data)
  },

  // Delete a job
  delete: async (id: string, options?: { reason?: string | null }): Promise<void> => {
    const reason = options?.reason?.trim()
    await api.delete(API_PATHS.jobs.delete(id), {
      data: reason ? { cancellation_reason: reason, reason, note: reason } : undefined,
      params: reason ? { cancellation_reason: reason } : undefined,
    })
  },

  // Lock a job (prevents further modifications)
  lock: async (id: string): Promise<Job> => {
    const response = await api.post<ApiResponse<Job>>(API_PATHS.jobs.lock(id))
    return response.data.data
  },

  // Unlock a job
  unlock: async (id: string): Promise<Job> => {
    const response = await api.post<ApiResponse<Job>>(API_PATHS.jobs.unlock(id))
    return response.data.data
  },

  // Update job status
  updateStatus: async (id: string, status: string): Promise<Job> => {
    const response = await api.post<ApiResponse<BackendJob>>(API_PATHS.jobs.status(id), { to_status: toBackendJobStatus(status) ?? status })
    return normalizeJob(response.data.data)
  },

  getMedia: async (jobId: string, mediaBaseUrl?: string | null): Promise<PaginatedResponse<JobMediaItem>> => {
    const response = await api.get<ApiResponse<PaginatedResponse<BackendJobMediaItem>> | PaginatedResponse<BackendJobMediaItem>>(
      API_PATHS.jobMedia.list,
      { params: { job: jobId, page: 1, page_size: 100 } },
    )
    const payload = 'data' in response.data ? response.data.data : response.data

    return {
      ...payload,
      results: payload.results.map((item) => normalizeJobMediaItem(item, mediaBaseUrl)),
    }
  },

  uploadMedia: async (
    jobId: string,
    file: File,
    options?: { caption?: string; is_before?: boolean; is_after?: boolean },
  ): Promise<JobMediaItem> => {
    const formData = new FormData()
    formData.append('job', jobId)
    formData.append('file', file)

    if (options?.caption) {
      formData.append('caption', options.caption)
    }

    formData.append('is_before', options?.is_before ? 'true' : 'false')
    formData.append('is_after', options?.is_after ? 'true' : 'false')

    const response = await api.post<ApiResponse<BackendJobMediaItem>>(API_PATHS.jobMedia.upload, formData)

    return normalizeJobMediaItem(response.data.data)
  },

  deleteMedia: async (id: string | number): Promise<void> => {
    await api.delete(API_PATHS.jobMedia.delete(id))
  },

  getManagementConfiguration: async (): Promise<JobManagementConfiguration> => {
    const response = await api.get<ApiResponse<BackendJobManagementConfiguration>>(API_PATHS.jobManagementConfiguration.current)
    return normalizeJobManagementConfiguration(response.data.data)
  },

  updateManagementConfiguration: async (payload: JobManagementConfigurationUpdatePayload): Promise<JobManagementConfiguration> => {
    const response = await api.post<ApiResponse<BackendJobManagementConfiguration>>(
      API_PATHS.jobManagementConfiguration.update,
      payload,
    )

    return normalizeJobManagementConfiguration(response.data.data)
  },

  getPayrollSummary: async (id: string, options?: { service_instance_id?: string | number }): Promise<JobPayrollSummary> => {
    const response = await api.get<ApiResponse<JobPayrollSummary>>(API_PATHS.jobs.payrollSummary(id), {
      params: options?.service_instance_id != null
        ? { service_instance_id: options.service_instance_id }
        : undefined,
    })
    return response.data.data
  },

  getAudits: async (params?: JobAuditQueryParams): Promise<PaginatedResponse<JobAuditEntry>> => {
    const response = await api.get<ApiResponse<PaginatedResponse<JobAuditEntry>> | PaginatedResponse<JobAuditEntry>>(
      API_PATHS.audits.list,
      { params },
    )
    return 'data' in response.data ? response.data.data : response.data
  },

  getAuditActionOptions: async (): Promise<JobAuditActionOption[]> => {
    const response = await api.get<ApiResponse<{ results?: JobAuditActionOption[] }> | { results?: JobAuditActionOption[] }>(
      API_PATHS.audits.actions,
    )
    const payload = 'data' in response.data ? response.data.data : response.data
    return Array.isArray(payload?.results) ? payload.results : []
  },

  // Generate next occurrence for a recurring job
  generateNextOccurrence: async (id: string): Promise<Job> => {
    const response = await api.post<ApiResponse<BackendJob>>(API_PATHS.jobs.generateNextOccurrence(id))
    return normalizeJob(response.data.data)
  },
}

export default jobsApi
